import { apiFetch } from './api';
/**
 * GamenicsManager — ゲームニクス × 行動心理学 統合管理
 *
 * 設計原則（ミクロ: ゲームニクス）:
 * - 全SE/エフェクトのパラメータはAdmin画面から動的制御（ハードコード禁止）
 * - 設定未定義時はフェイルセーフ（無音・無演出で続行）
 * - AIは音源を合成しない（ロジック設計のみ）
 * - 褒める演出の直前に「一瞬の間」を挿入
 *
 * 設計原則（マクロ: 行動心理学）:
 * - 可変報酬（フックモデル）: 報酬のタイミング・内容を予測不可能にする
 * - 損失回避: ストリーク喪失警告で離脱を防止する
 * - ツァイガルニク効果: 未完了タスクをシステム内に維持する
 * - 希少性（オクタリシス）: タイマー・人数制限で即時行動を促す
 *
 * @see docs/GAMENICS_DESIGN_GUIDELINES.md
 */

// ============================================================
// Types
// ============================================================

export interface GamenicsSoundConfig {
  url: string | null;
  volume: number;
}

export interface GamenicsEffectConfig {
  type: string;
  duration_ms: number;
  particle_count: number;
  particle_size: number;
  color_primary: string;
  color_secondary: string;
  scale: number;
  delay_before_ms: number;
}

export interface PsychologyParams {
  reward_batch_delay_min_ms: number;
  reward_batch_delay_max_ms: number;
  slot_animation_duration_ms: number;
  reward_reveal_interval_ms: number;
  streak_warning_threshold_hours: number;
  streak_fire_icon_threshold: number;
  scarcity_timer_enabled: boolean;
  scarcity_urgency_threshold_hours: number;
  profile_completion_cap_percent: number;
  voice_analysis_required_recordings: number;
}

export interface GamenicsConfig {
  sounds: Record<string, GamenicsSoundConfig>;
  effects: Record<string, GamenicsEffectConfig>;
  psychology: Partial<PsychologyParams>;
}

export interface UserProficiency {
  totalSubmissions: number;
  totalLikesReceived: number;
  totalVotesCast: number;
  proficiencyLevel: number; // 1=Beginner, 2=Intermediate, 3=Advanced
  badges: string[];
  streakDays: number;           // 連続投稿日数（損失回避ドライブ）
  streakMaxDays: number;        // 最大連続日数（実績表示用）
  profileCompletionPercent: number; // プロフィール完成度（ツァイガルニク効果）
}

export interface UserGoal {
  id: string;
  goalType: string;
  displayText: string;
  targetValue: number;
  currentValue: number;
  isCompleted: boolean;
  priority: number;
}

// ============================================================
// Event Keys — 全イベントの定数定義
// ============================================================

export const GamenicsEvent = {
  // フェーズA: オンボーディング
  TAP: 'tap',
  TAP_CONFIRM: 'tap_confirm',
  TAP_CANCEL: 'tap_cancel',
  SCREEN_TRANSITION: 'screen_transition',

  // フェーズB: コア体験
  RECORDING_START: 'recording_start',
  RECORDING_STOP: 'recording_stop',
  SUBMISSION_COMPLETE: 'submission_complete',
  BATTLE_START: 'battle_start',
  LIKE_RECEIVED: 'like_received',

  // フェーズB: 可変報酬（行動心理学）
  SLOT_REVEAL: 'slot_reveal',

  // フェーズC: マイルストーン
  MILESTONE_10: 'milestone_10',
  MILESTONE_20: 'milestone_20',
  MILESTONE_30: 'milestone_30',

  // フェーズC: 損失回避（行動心理学）
  STREAK_WARNING: 'streak_warning',
} as const;

export type GamenicsEventKey = typeof GamenicsEvent[keyof typeof GamenicsEvent];

// ============================================================
// Proficiency Levels — 習熟度定数
// ============================================================

export const ProficiencyLevel = {
  BEGINNER: 1,      // 0〜9 投稿
  INTERMEDIATE: 2,  // 10〜29 投稿
  ADVANCED: 3,      // 30+ 投稿
} as const;

/**
 * 投稿数から習熟度レベルを算出
 */
export function calculateProficiencyLevel(submissionCount: number): number {
  if (submissionCount >= 30) return ProficiencyLevel.ADVANCED;
  if (submissionCount >= 10) return ProficiencyLevel.INTERMEDIATE;
  return ProficiencyLevel.BEGINNER;
}

// ============================================================
// GamenicsManager Singleton
// ============================================================

class GamenicsManagerImpl {
  private soundConfigs: Record<string, GamenicsSoundConfig> = {};
  private effectConfigs: Record<string, GamenicsEffectConfig> = {};
  private psychologyParams: Partial<PsychologyParams> = {};
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private isLoaded = false;
  private loadPromise: Promise<void> | null = null;

  // ============================================================
  // Config Loading
  // ============================================================

  /**
   * Admin画面から設定されたゲームニクス + 心理学パラメータをAPIから取得
   * フェイルセーフ: API失敗時は空オブジェクトで初期化（全機能が動作継続）
   */
  async loadConfig(): Promise<void> {
    // 重複ロード防止
    if (this.loadPromise) return this.loadPromise;

    // デバッグ用のシンセサイザー音源（確実になるようにURLではなく内部命令にする）
    const fallbackSounds = {
      tap: { url: 'synth:pop', volume: 0.5 },
      tap_confirm: { url: 'synth:success', volume: 0.6 },
      tap_cancel: { url: 'synth:cancel', volume: 0.5 },
      submission_complete: { url: 'synth:chime', volume: 0.8 },
      battle_start: { url: 'synth:bass', volume: 0.7 },
    };

    this.loadPromise = (async () => {
      try {
        const res = await apiFetch('/api/gamenics/config');
        if (!res.ok) {
          console.warn('[Gamenics] Config API returned', res.status, '— using fallback sounds');
          this.soundConfigs = fallbackSounds;
          this.isLoaded = true;
          return;
        }
        const data: GamenicsConfig = await res.json();
        
        // APIが空データを返した場合もフォールバックを使う
        this.soundConfigs = Object.keys(data.sounds ?? {}).length > 0 ? data.sounds! : fallbackSounds;
        this.effectConfigs = data.effects ?? {};
        this.psychologyParams = data.psychology ?? {};
        this.preloadSounds();
        this.isLoaded = true;
      } catch (e) {
        console.warn('[Gamenics] Config API returned error — using fallback sounds', e);
        this.soundConfigs = fallbackSounds;
        this.isLoaded = true;
      }
    })();

    return this.loadPromise;
  }

  // ============================================================
  // ミクロ層: SE再生・エフェクト（ゲームニクス）
  // ============================================================

  /**
   * SE再生
   * フェイルセーフ: 設定がnull/undefined/空 → 無音で次の処理へ
   * ガイドライン: 全タップ操作に固有SE。無音操作は一切禁止（設定があれば）。
   */
  async playSound(eventKey: string): Promise<void> {
    const config = this.soundConfigs[eventKey];
    if (!config?.url) return;

    try {
      // Ignored synth fallback to comply with design guidelines
      if (config.url.startsWith('synth:')) {
        console.warn(`[Gamenics] Synthesizer sounds are prohibited. Skipping "${config.url}".`);
        return;
      }

      let audio = this.audioCache.get(eventKey);
      if (!audio) {
        audio = new Audio(config.url);
        this.audioCache.set(eventKey, audio);
      }
      audio.volume = config.volume ?? 1.0;
      audio.currentTime = 0;
      
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => console.warn(`[Gamenics] Audio AutoPlay blocked or URL invalid for "${eventKey}":`, e));
      }
    } catch (e) {
      console.warn(`[Gamenics] Sound play failed for "${eventKey}":`, e);
    }
  }

  /**
   * 「褒める」演出（間 + SE + エフェクト同期）
   * ガイドライン: 「褒める前に一瞬全てを止めて、感激直前の『間』を作る」
   */
  async triggerCelebration(eventKey: string): Promise<void> {
    const effectConfig = this.effectConfigs[eventKey];
    const delayMs = effectConfig?.delay_before_ms ?? 200;
    await this.freeze(delayMs);

    await Promise.all([
      this.playSound(eventKey),
      this.showEffect(effectConfig),
    ]);
  }

  /**
   * 視覚エフェクト表示
   * フェイルセーフ: 設定なし → 無演出で進行
   */
  private async showEffect(
    config: GamenicsEffectConfig | undefined
  ): Promise<void> {
    if (!config) return;

    try {
      const event = new CustomEvent('gamenics:effect', {
        detail: {
          type: config.type,
          duration: config.duration_ms,
          count: config.particle_count,
          size: config.particle_size ?? 1.0,
          colors: [
            config.color_primary ?? '#FFD700',
            config.color_secondary ?? '#FF6B6B',
          ],
          scale: config.scale ?? 1.0,
        },
      });
      window.dispatchEvent(event);
    } catch (e) {
      console.warn('[Gamenics] Effect render failed:', e);
    }
  }

  // ============================================================
  // マクロ層: 可変報酬・損失回避・ストリーク（行動心理学）
  // ============================================================

  /**
   * 可変報酬演出（フックモデル）
   *
   * フロー:
   * 1. スロット的「解析中...」演出（slot_animation_duration_ms）
   * 2. 褒める前の「間」
   * 3. 報酬を段階的に小出し表示（reward_reveal_interval_ms 間隔）
   *
   * 報酬のタイミング・内容は毎回変動（変動比率スケジュール）。
   * フェイルセーフ: パラメータ未設定時はデフォルト値で動作。
   */
  async triggerVariableReward(rewards: Array<{ type: string; data: unknown }>): Promise<void> {
    if (!rewards || rewards.length === 0) return;

    try {
      // 1. スロット演出 SE + エフェクト
      const slotDuration = this.getPsychParam('slot_animation_duration_ms', 2000);
      await this.playSound(GamenicsEvent.SLOT_REVEAL);
      await this.showEffect(this.effectConfigs[GamenicsEvent.SLOT_REVEAL]);
      await this.freeze(slotDuration);

      // 2. 褒める前の「間」
      await this.freeze(200);

      // 3. 報酬を段階表示（予測不可能なタイミングで）
      const revealInterval = this.getPsychParam('reward_reveal_interval_ms', 800);
      for (const reward of rewards) {
        // 各報酬表示のイベント発行
        window.dispatchEvent(new CustomEvent('gamenics:reward-reveal', {
          detail: reward,
        }));

        // 段階表示の間にSEを鳴らす
        await this.playSound(GamenicsEvent.LIKE_RECEIVED);

        // 次の報酬まで少しランダムな遅延（可変性の演出）
        const jitter = Math.random() * 200; // 0〜200msのランダム揺れ
        await this.freeze(revealInterval + jitter);
      }
    } catch (e) {
      // 【フェイルセーフ】可変報酬演出失敗しても処理を止めない
      console.warn('[Gamenics] Variable reward failed:', e);
    }
  }

  /**
   * ストリーク喪失警告チェック（損失回避）
   *
   * ガイドライン: 「今日の声を残さないと、連続記録（〇〇日）が失われます」
   * 利益訴求ではなく、喪失の痛みを強調するフレーミング。
   *
   * @returns 警告が必要ならtrue + 警告データ、不要ならfalse
   */
  checkStreakWarning(streakDays: number, lastPostDate: string | null): {
    shouldWarn: boolean;
    message: string;
    hoursRemaining: number;
  } {
    if (!lastPostDate || streakDays <= 0) {
      return { shouldWarn: false, message: '', hoursRemaining: 0 };
    }

    try {
      const thresholdHours = this.getPsychParam('streak_warning_threshold_hours', 4);
      const fireThreshold = this.getPsychParam('streak_fire_icon_threshold', 3);

      const lastDate = new Date(lastPostDate + 'T23:59:59');
      const now = new Date();
      const hoursRemaining = Math.max(0, (lastDate.getTime() - now.getTime()) / (1000 * 60 * 60));

      if (hoursRemaining <= thresholdHours && hoursRemaining > 0) {
        const fireIcon = streakDays >= fireThreshold ? '🔥' : '';
        return {
          shouldWarn: true,
          // 損失回避フレーミング: 利益ではなく損失を強調
          message: `${fireIcon}今日の声を残さないと、連続記録（${streakDays}日）が失われます`,
          hoursRemaining: Math.ceil(hoursRemaining),
        };
      }

      return { shouldWarn: false, message: '', hoursRemaining: Math.ceil(hoursRemaining) };
    } catch (e) {
      // 【フェイルセーフ】計算失敗時は警告しない
      console.warn('[Gamenics] Streak warning check failed:', e);
      return { shouldWarn: false, message: '', hoursRemaining: 0 };
    }
  }

  /**
   * 可変報酬バッチ用のランダム遅延を計算（フックモデル）
   * reward_batch_delay_min_ms 〜 reward_batch_delay_max_ms の範囲でランダム
   */
  getRandomBatchDelay(): number {
    const min = this.getPsychParam('reward_batch_delay_min_ms', 1000);
    const max = this.getPsychParam('reward_batch_delay_max_ms', 5000);
    return min + Math.random() * (max - min);
  }

  /**
   * 心理パラメータの取得（型安全・フェイルセーフ）
   * Admin画面で未設定の場合はデフォルト値を返す
   */
  getPsychParam(key: keyof PsychologyParams, defaultValue: number): number;
  getPsychParam(key: keyof PsychologyParams, defaultValue: boolean): boolean;
  getPsychParam(key: keyof PsychologyParams, defaultValue: number | boolean): number | boolean {
    const value = this.psychologyParams[key];
    if (value === undefined || value === null) return defaultValue;
    return value;
  }

  // ============================================================
  // Internal Utilities
  // ============================================================

  /**
   * 画面フリーズ（褒める前の「間」）
   * ガイドライン: ミリ秒単位のフリーズや無音
   */
  private freeze(ms: number): Promise<void> {
    if (ms <= 0) return Promise.resolve();
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 音源のプリロード（初回config読み込み時に実行）
   */
  private preloadSounds(): void {
    for (const [key, config] of Object.entries(this.soundConfigs)) {
      if (config.url) {
        try {
          const audio = new Audio();
          audio.preload = 'auto';
          audio.src = config.url;
          this.audioCache.set(key, audio);
        } catch (e) {
          console.warn(`[Gamenics] Preload failed for "${key}":`, e);
        }
      }
    }
  }

  /** 設定読み込み済みかどうか */
  get loaded(): boolean {
    return this.isLoaded;
  }

  /** キャッシュクリア（Admin画面での設定変更後に呼ぶ） */
  clearCache(): void {
    this.audioCache.forEach(audio => {
      audio.pause();
      audio.src = '';
    });
    this.audioCache.clear();
    this.soundConfigs = {};
    this.effectConfigs = {};
    this.psychologyParams = {};
    this.isLoaded = false;
    this.loadPromise = null;
  }
}

// シングルトンインスタンス
export const GamenicsManager = new GamenicsManagerImpl();
